import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth } from "../../context/AuthContext";
import BaseModal from "./BaseModal";

export default function SecurityModal({ onClose, onSuccess }) {
  const { t } = useTranslation();
  const { user, resetPassword } = useAuth();
  const [step, setStep] = useState("phone"); // "phone" | "verify" | "password"
  const [phone, setPhone] = useState(user?.phone || "");
  const [verificationCode, setVerificationCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const sendVerificationCode = async () => {
    setIsLoading(true);
    setError("");
    try {
      // Simulate SMS sending
      await new Promise(resolve => setTimeout(resolve, 1000));
      setStep("verify");
    } catch (err) {
      setError(t("apiErrors.INTERNAL_ERROR"));
    } finally {
      setIsLoading(false);
    }
  };

  const verifyCode = async () => {
    setIsLoading(true);
    setError("");
    try {
      // Simulate verification
      if (verificationCode === "123456") {
        setStep("password");
      } else {
        setError(t("apiErrors.AUTH_OTP_ERROR"));
      }
    } catch (err) {
      setError(t("apiErrors.INTERNAL_ERROR"));
    } finally {
      setIsLoading(false);
    }
  };

  const changePassword = async () => {
    setIsLoading(true);
    setError("");
    try {
      if (newPassword.length < 6) {
        setError(t("securityModal.errors.password"));
        return;
      }
      await resetPassword({ phone, newPassword });
      onSuccess?.();
      onClose?.();
    } catch (err) {
      setError(t("apiErrors.INTERNAL_ERROR"));
    } finally {
      setIsLoading(false);
    }
  };

  const stepVariants = {
    initial: { opacity: 0, x: 20 },
    animate: { opacity: 1, x: 0 },
    exit: { opacity: 0, x: -20 },
  };

  return (
    <BaseModal
      title={t("securityModal.title")}
      onClose={onClose}
      footer={null}
      maxWidthClass="max-w-md"
    >
      <div className="relative overflow-hidden min-h-[360px] py-2">
        <AnimatePresence mode="wait">
          {step === "phone" && (
            <motion.div
              key="phone"
              variants={stepVariants}
              initial="initial"
              animate="animate"
              exit="exit"
              className="space-y-8"
            >
              <div className="text-center">
                <div className="w-20 h-20 bg-blue-50 dark:bg-blue-900/20 rounded-[24px] flex items-center justify-center mx-auto mb-6 shadow-lg shadow-blue-500/10">
                  <span className="material-symbols-outlined text-blue-600 text-3xl">phone_android</span>
                </div>
                <h3 className="text-xl font-black text-slate-900 dark:text-white italic uppercase tracking-tight mb-2">
                  {t("auth.phoneMode")}
                </h3>
                <p className="text-sm font-bold text-slate-500 dark:text-slate-400">
                  {t("securityModal.phoneHelp")}
                </p>
              </div>

              <div className="space-y-3">
                <label className="text-[10px] font-black text-slate-500 dark:text-slate-400 uppercase tracking-[0.2em] px-1">
                  {t("auth.phone")}
                </label>
                <div className="relative">
                  <span className="material-symbols-outlined absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 text-[20px]">phone</span>
                  <input
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    type="tel"
                    placeholder="+998901234567"
                    className="w-full h-14 rounded-2xl bg-slate-50 dark:bg-slate-800 border-2 border-transparent focus:border-[#1e9f6b] dark:text-white pl-12 pr-4 outline-none transition-all font-black italic"
                  />
                </div>
              </div>

              {error && (
                <p className="text-xs font-bold text-red-500 px-1 animate-pulse">
                  {error}
                </p>
              )}

              <button
                type="button"
                onClick={sendVerificationCode}
                disabled={isLoading || !phone.trim()}
                className="w-full h-14 rounded-2xl bg-[#1e9f6b] hover:bg-[#0d7a52] disabled:opacity-50 text-white font-black uppercase tracking-widest text-sm shadow-xl shadow-[#1e9f6b]/30 transition-all flex items-center justify-center gap-3"
              >
                {isLoading ? (
                  <span className="h-5 w-5 border-3 border-white/30 border-t-white rounded-full animate-spin" />
                ) : (
                  <>
                    {t("auth.submit")}
                    <span className="material-symbols-outlined text-[18px]">arrow_forward</span>
                  </>
                )}
              </button>
            </motion.div>
          )}

          {step === "verify" && (
            <motion.div
              key="verify"
              variants={stepVariants}
              initial="initial"
              animate="animate"
              exit="exit"
              className="space-y-8"
            >
              <div className="text-center">
                <div className="w-20 h-20 bg-emerald-50 dark:bg-emerald-900/20 rounded-[24px] flex items-center justify-center mx-auto mb-6 shadow-lg shadow-emerald-500/10">
                  <span className="material-symbols-outlined text-emerald-600 text-3xl">sms</span>
                </div>
                <h3 className="text-xl font-black text-slate-900 dark:text-white italic uppercase tracking-tight mb-2">
                  {t("securityModal.verification")}
                </h3>
                <p className="text-sm font-bold text-slate-500 dark:text-slate-400">
                  {phone} {t("securityModal.codeSent")}
                </p>
              </div>

              <div className="space-y-3">
                <label className="text-[10px] font-black text-slate-500 dark:text-slate-400 uppercase tracking-[0.2em] px-1">
                  {t("securityModal.otpCode")}
                </label>
                <input
                  value={verificationCode}
                  onChange={(e) => setVerificationCode(e.target.value)}
                  type="text"
                  placeholder="123456"
                  maxLength={6}
                  className="w-full h-16 rounded-2xl bg-slate-50 dark:bg-slate-800 border-2 border-transparent focus:border-[#1e9f6b] dark:text-white px-4 text-center text-3xl font-black italic tracking-[0.5em] outline-none transition-all"
                />
                <p className="text-[10px] text-slate-400 text-center font-bold tracking-widest opacity-60">TEST: 123456</p>
              </div>

              {error && (
                <p className="text-xs font-bold text-red-500 px-1 text-center animate-pulse">
                  {error}
                </p>
              )}

              <div className="flex gap-4">
                <button
                  type="button"
                  onClick={() => setStep("phone")}
                  className="flex-1 h-14 rounded-2xl border-2 border-slate-100 dark:border-slate-800 text-slate-500 font-black uppercase tracking-widest text-[10px] hover:bg-slate-50 dark:hover:bg-slate-800 transition-all"
                >
                  {t("auth.back")}
                </button>
                <button
                  type="button"
                  onClick={verifyCode}
                  disabled={isLoading || verificationCode.length !== 6}
                  className="flex-[2] h-14 rounded-2xl bg-[#1e9f6b] hover:bg-[#0d7a52] disabled:opacity-50 text-white font-black uppercase tracking-widest text-sm shadow-xl shadow-[#1e9f6b]/30 transition-all flex items-center justify-center gap-3"
                >
                  {isLoading ? (
                    <span className="h-5 w-5 border-3 border-white/30 border-t-white rounded-full animate-spin" />
                  ) : (
                    <>
                      {t("common.done")}
                      <span className="material-symbols-outlined text-[18px]">verified_user</span>
                    </>
                  )}
                </button>
              </div>
            </motion.div>
          )}

          {step === "password" && (
            <motion.div
              key="password"
              variants={stepVariants}
              initial="initial"
              animate="animate"
              exit="exit"
              className="space-y-8"
            >
              <div className="text-center">
                <div className="w-20 h-20 bg-amber-50 dark:bg-amber-900/20 rounded-[24px] flex items-center justify-center mx-auto mb-6 shadow-lg shadow-amber-500/10">
                  <span className="material-symbols-outlined text-amber-600 text-3xl">lock_reset</span>
                </div>
                <h3 className="text-xl font-black text-slate-900 dark:text-white italic uppercase tracking-tight mb-2">
                  {t("securityModal.newPassword")}
                </h3>
                <p className="text-sm font-bold text-slate-500 dark:text-slate-400">
                  {t("securityModal.passwordHelp")}
                </p>
              </div>

              <div className="space-y-3">
                <label className="text-[10px] font-black text-slate-500 dark:text-slate-400 uppercase tracking-[0.2em] px-1">
                  {t("securityModal.newPassword")}
                </label>
                <div className="relative">
                  <span className="material-symbols-outlined absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 text-[20px]">key</span>
                  <input
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    type="password"
                    placeholder="••••••••"
                    className="w-full h-14 rounded-2xl bg-slate-50 dark:bg-slate-800 border-2 border-transparent focus:border-[#1e9f6b] dark:text-white pl-12 pr-4 outline-none transition-all font-black tracking-widest"
                  />
                </div>
              </div>

              {error && (
                <p className="text-xs font-bold text-red-500 px-1 animate-pulse">
                  {error}
                </p>
              )}

              <div className="flex gap-4">
                <button
                  type="button"
                  onClick={() => setStep("verify")}
                  className="flex-1 h-14 rounded-2xl border-2 border-slate-100 dark:border-slate-800 text-slate-500 font-black uppercase tracking-widest text-[10px] hover:bg-slate-50 dark:hover:bg-slate-800 transition-all"
                >
                  {t("auth.back")}
                </button>
                <button
                  type="button"
                  onClick={changePassword}
                  disabled={isLoading || newPassword.length < 6}
                  className="flex-[2] h-14 rounded-2xl bg-[#1e9f6b] hover:bg-[#0d7a52] disabled:opacity-50 text-white font-black uppercase tracking-widest text-sm shadow-xl shadow-[#1e9f6b]/30 transition-all flex items-center justify-center gap-3"
                >
                  {isLoading ? (
                    <span className="h-5 w-5 border-3 border-white/30 border-t-white rounded-full animate-spin" />
                  ) : (
                    <>
                      {t("common.save")}
                      <span className="material-symbols-outlined text-[18px]">save</span>
                    </>
                  )}
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </BaseModal>
  );
}


